import React, { useMemo } from 'react';
import { ResolveMediaUrl } from '../../../../utilities/BL/RunToGoal';

/**
 * Portada de inicio dinámica y configurable para Tiro a Gol V2 (RunToGoal).
 * Adapta logotipos de marca y patrocinador, título, subtítulo, fondo y elemento animado
 * de acuerdo con el tema activo seleccionado.
 * 
 * @param {Object} props
 * @param {boolean} [props.isActiveWomensMode] - Modo femenil / varonil.
 * @param {Object} [props.theme] - Tema visual inyectado.
 * @param {Object} [props.currentMatchState] - Estado de la partida actual.
 */
const FrontPageRunToGoal = ({ isActiveWomensMode, theme: propTheme, currentMatchState }) => {
    const activeTheme = useMemo(() => {
        const raw = currentMatchState?.theme || propTheme || currentMatchState?.Theme || null;
        if (!raw) return null;
        if (typeof raw === 'string') {
            try {
                return JSON.parse(raw);
            } catch (e) {
                console.warn('[FrontPageRunToGoal] Error al parsear JSON del tema:', e);
                return null;
            }
        }
        return raw;
    }, [currentMatchState, propTheme]);

    const gameModeType = activeTheme?.gameModeType || 'soccer';
    const coverTitle = activeTheme?.coverTitle || 'TIRO A GOL';
    const coverSubtitle = activeTheme?.coverSubtitle || 'Presentan';
    const coverTextColor = activeTheme?.coverTextColor || '#ffffff';

    const brandLogoUrl = activeTheme?.brandLogoUrl ? ResolveMediaUrl(activeTheme.brandLogoUrl) : '';
    const sponsorLogoUrl = activeTheme?.sponsorLogoUrl ? ResolveMediaUrl(activeTheme.sponsorLogoUrl) : '';

    const coverBackgroundUrl = activeTheme?.coverBackgroundUrl
        ? ResolveMediaUrl(activeTheme.coverBackgroundUrl)
        : '';
    const coverBackgroundColor = activeTheme?.coverBackgroundColor || '';

    // Elemento que cae y rebota en la portada (balón, coche, corredor)
    const coverElementUrl = useMemo(() => {
        if (activeTheme?.outroElementUrl) return ResolveMediaUrl(activeTheme.outroElementUrl);
        if (activeTheme?.ballImageUrl) return ResolveMediaUrl(activeTheme.ballImageUrl);

        if (gameModeType === 'cars') {
            return '/img/games/runtogoal/redbull/xolo-rojo.png';
        }
        if (gameModeType === 'runners') {
            return '/img/games/runtogoal/carrera-atleti/lucho.png';
        }
        return '/img/games/runtogoal/balon-grande.png';
    }, [activeTheme, gameModeType]);

    return (
        <>
            {/* FONDO DINÁMICO */}
            <div
                className="FrontPage--Riddle__Screen__Background"
                style={{
                    ...(coverBackgroundUrl ? { backgroundImage: `url("${coverBackgroundUrl}")`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}),
                    ...(coverBackgroundColor ? { backgroundColor: coverBackgroundColor } : {})
                }}
            >
                {!coverBackgroundUrl && !coverBackgroundColor && (
                    <img
                        src="/img/games/runtogoal/redbull/background-frontpage.png"
                        className="FrontPage--Riddle__Screen__Background__Image"
                        alt="Fondo"
                    />
                )}
            </div>

            <div className="goal-intro">
                <div className="goal-intro__logo-container">
                    {/* Logotipos de Marca y Patrocinador */}
                    <div className="d-flex align-items-center justify-content-center flex-wrap" style={{ gap: '24px', marginBottom: '16px' }}>
                        {brandLogoUrl ? (
                            <img
                                src={brandLogoUrl}
                                className="LogoBrand"
                                style={{ maxWidth: '350px', maxHeight: '140px', objectFit: 'contain' }}
                                alt="Brand Logo"
                            />
                        ) : (
                            <img
                                src="/img/page/xolos-five.png"
                                className="LogoADSLxZtudia"
                                style={{ maxWidth: '400px', width: '100%', objectFit: 'contain' }}
                                alt="Default Brand Logo"
                            />
                        )}
                        {sponsorLogoUrl && (
                            <img
                                src={sponsorLogoUrl}
                                className="LogoSponsor"
                                style={{ maxWidth: '350px', maxHeight: '140px', objectFit: 'contain' }}
                                alt="Sponsor Logo"
                            />
                        )}
                    </div>

                    {coverSubtitle && (
                        <p
                            className="goal-intro__text text-uppercase fw-bold"
                            style={{
                                color: coverTextColor,
                                textShadow: '-3px 0 rgba(0,0,0,0.8), 0 3px rgba(0,0,0,0.8), 3px 0 rgba(0,0,0,0.8), 0 -3px rgba(0,0,0,0.8)'
                            }}
                        >
                            {coverSubtitle}
                        </p>
                    )}

                    <p
                        className="goal-intro__title fw-bold text-uppercase"
                        style={{
                            color: coverTextColor,
                            textShadow: '-6px 0 #ef4444, 0 6px #ef4444, 6px 0 #ef4444, 0 -6px #ef4444',
                            fontSize: 'clamp(4rem, 8vw, 9.4rem)',
                            lineHeight: 1.1
                        }}
                    >
                        {coverTitle}
                    </p>
                </div>

                {/* Elemento animado que cae del cielo y rebota */}
                {coverElementUrl && (
                    <img
                        src={coverElementUrl}
                        className="goal-intro__ball"
                        alt="Portada Elemento"
                        style={{
                            width: gameModeType === 'soccer' ? '350px' : '450px',
                            maxWidth: '80vw'
                        }}
                    />
                )}

                <p
                    className="FooterCredits"
                    style={{
                        position: 'fixed',
                        bottom: '0',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '30px',
                        color: coverTextColor === '#ffffff' ? '#ffffff' : '#000000',
                        textShadow: '0 2px 4px rgba(0,0,0,0.6)',
                        zIndex: 100
                    }}
                >
                    Hecho con ♥ por Interaktia | 2026
                </p>
            </div>
        </>
    );
};

export { FrontPageRunToGoal };